import irmHeader from 'assets/images/irm-header.png';
import { patientBarcodeStrip, barcodeScriptBlock, resolveCoupleSpouseCodes } from 'utils/printBarcodes';

// ── Shared helpers (duplicated per repo convention — each print file owns these) ──

function amountInWords(amount) {
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Math.round(amount * 100));
  const paise = n % 100; const rupees = Math.floor(n / 100);
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (num) => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+toW(num%100) : '');
  };
  const inIndian = (num) => {
    if (num === 0) return 'Zero'; let out = '';
    if (num >= 10000000) { out += toW(Math.floor(num/10000000))+' Crore '; num %= 10000000; }
    if (num >= 100000)   { out += toW(Math.floor(num/100000))  +' Lakh ';  num %= 100000; }
    if (num >= 1000)     { out += toW(Math.floor(num/1000))    +' Thousand '; num %= 1000; }
    if (num > 0)         { out += toW(num); } return out.trim();
  };
  let out = inIndian(rupees);
  if (paise > 0) out += ` and ${toW(paise)} Paise`;
  return 'INR ' + out.trim();
}

const REFUND_MODE_LABEL = { CASH: 'Cash', CREDIT_NOTE: 'Credit Note', ONLINE: 'Online Transfer' };
const REFUND_MODE_STYLE = {
  CASH:        { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  CREDIT_NOTE: { bg: '#ede7f6', color: '#4527a0', border: '#b39ddb' },
  ONLINE:      { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
};

/**
 * Print a Return Note — branded to match printGrn/printBill/printCreditNote/
 * printDebitNote/printPaymentReceipt (irmHeader logo, Montserrat title, #b2d8de
 * bordered letterhead, A5 portrait, PAN/bank/seal footer).
 * ret : return object shaped like BillingRepository#getReturn() — carries
 *       `.original_bill` (bill_no, patient_*, net_amount, bill_date) and
 *       `.items` (return line items — item_name/variant_name/batch_no are
 *       denormalized directly on the joined bill item).
 * ph  : selected pharmacy object (company/letterhead details)
 */
export default function printReturnNote(ret, ph) {
  if (!ret) return;
  const PRIMARY = '#00798E';
  const LIGHT_BG = '#e0f4f7';
  const BORDER = '#b2d8de';
  const bill = ret.original_bill || {};
  const billItems = bill.items || [];
  const fmtD = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;

  const modeStyle = REFUND_MODE_STYLE[ret.refund_method] || REFUND_MODE_STYLE.CASH;
  const modeLabel = REFUND_MODE_LABEL[ret.refund_method] || 'Cash';

  // Barcodes get their own full-height column (rowspan) beside the
  // pharmacy/return-meta info, instead of being crammed under the patient
  // mobile — keeps both left columns a natural, even height.
  const { coupleCode, spouseCode } = resolveCoupleSpouseCodes(bill.contactInfo);
  const { html: barcodeHtml, initScript: barcodeInit } = patientBarcodeStrip({
    idPrefix: 'bc-ret',
    patientCode: bill.contactInfo?.external_code,
    coupleCode,
    spouseCode,
    documentCode: ret.return_no,
    documentLabel: 'Return No',
  });
  const barcodeCell = barcodeHtml
    ? `<td rowspan="2" style="width:22%;padding:5px 8px;vertical-align:top">${barcodeHtml}</td>`
    : '';
  const leftColWidth = barcodeHtml ? '39%' : '50%';

  const items = (ret.items || []).map(ri => {
    const bi = billItems.find(b => b.id === ri.irm_pharm_bill_items_id) || {};
    return {
      name: bi.item_name || ri._name || '—',
      variant: bi.variant_name || '',
      batch: bi.batch_no || '—',
      qty: ri.return_qty,
      refund: Number(ri.refund_amount || 0),
    };
  });
  const total = items.reduce((s, i) => s + i.refund, 0);

  const itemRows = items.map((it, i) => `
    <tr style="${i % 2 === 0 ? '' : `background:${LIGHT_BG}66`}">
      <td style="${C}text-align:center">${i + 1}</td>
      <td style="${C}"><span style="font-weight:600">${it.name}</span>${it.variant ? ` <small style="color:#777">(${it.variant})</small>` : ''}</td>
      <td style="${C}font-family:monospace;text-align:center">${it.batch}</td>
      <td style="${C}text-align:center;font-family:monospace">${it.qty}</td>
      <td style="${C}text-align:right;font-family:monospace;font-weight:600">${it.refund.toFixed(2)}</td>
    </tr>`).join('');

  const body = `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">RETURN NOTE</div>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER}">
      <tr>
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:11pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
          ${ph?.sub_title ? `<div style="font-size:7.5pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
          <div style="font-size:7pt;color:#333;margin-top:2px;line-height:1.5">${ph?.address || ''}${ph?.city ? ', '+ph.city : ''}${ph?.pincode ? ' - '+ph.pincode : ''}</div>
          ${ph?.dl_no ? `<div style="font-size:6.5pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
          ${ph?.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
          ${ph?.state ? `<div style="font-size:6.5pt;color:#555">State Name : ${ph.state}${ph.state_code ? ', Code : '+ph.state_code : ''}</div>` : ''}
          ${ph?.mobile ? `<div style="font-size:6.5pt;color:#555">Mobile : ${ph.mobile}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;vertical-align:top;${barcodeHtml ? `border-right:1px solid ${BORDER}` : ''}">
          <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Return No.</td><td style="font-weight:700;padding-bottom:2px">${ret.return_no || '—'}</td></tr>
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Return Date</td><td style="font-weight:700;padding-bottom:2px">${fmtD(ret.return_date)}</td></tr>
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Refund Mode</td><td style="padding-bottom:2px"><span style="padding:2px 8px;border-radius:3px;font-size:7pt;font-weight:700;background:${modeStyle.bg};color:${modeStyle.color}">${modeLabel}</span></td></tr>
            ${ret.reason ? `<tr><td style="color:#777;padding-right:8px;white-space:nowrap;vertical-align:top">Reason</td><td style="font-size:7pt;color:#555">${ret.reason}</td></tr>` : ''}
          </table>
        </td>
        ${barcodeCell}
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Patient</div>
          <div style="font-weight:700;font-size:9pt">${bill.patient_name || '—'}</div>
          ${bill.patient_mobile ? `<div style="font-size:7.5pt;color:#555">Mob: ${bill.patient_mobile}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Original Bill</div>
          <div style="font-weight:700">${bill.bill_no || '—'}</div>
          <div style="font-size:7.5pt;color:#777">Bill Amount: ₹${Number(bill.net_amount || 0).toFixed(2)}</div>
          <div style="font-size:7.5pt;color:#777">Bill Date: ${fmtD(bill.bill_date)}</div>
        </td>
      </tr>
    </table>
    ${barcodeScriptBlock(barcodeInit)}
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;margin-top:0;table-layout:fixed">
      <tr>
        <th style="${CH}text-align:center;width:5%">#</th>
        <th style="${CH}text-align:left">Item</th>
        <th style="${CH}text-align:center;width:16%">Batch</th>
        <th style="${CH}text-align:center;width:10%">Qty</th>
        <th style="${CH}text-align:right;width:16%">Refund (₹)</th>
      </tr>
      ${itemRows}
      <tr style="background:${LIGHT_BG}">
        <td colspan="4" style="${C}text-align:right;font-weight:700">Total Refund</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:11pt;color:${PRIMARY}">${total.toFixed(2)}</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount (in words)</td>
        <td style="padding:4px 10px;font-style:italic">${amountInWords(total)} Only</td>
        <td style="padding:4px 7px;border-left:1px solid ${BORDER};font-size:7pt;color:#888;text-align:right;white-space:nowrap">E. &amp; O.E.</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:70%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.pan_no ? `<div style="margin-bottom:3px"><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
          ${ph?.declaration ? `<div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-top:${ph.pan_no?'4px':'0'};margin-bottom:2px">Declaration</div><div style="font-size:6.5pt;color:#555;line-height:1.5">${ph.declaration}</div>` : ''}
        </td>
        <td style="width:30%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:16px">
          <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:16px">for ${ph?.name || ''}</div>
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
      </tr>
    </table>
  `;

  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#00798E;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Return Note &mdash; ${ret.return_no || bill.bill_no || ''}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#00798E;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Return Note ${ret.return_no || bill.bill_no || ''}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:484px;margin:0 auto">${body}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}
